import {
  getQuotePreview,
  getProformaPreview,
  getContractPreview,
  getUserTaxProfile,
} from '@flecto/api/shared'
import { FlectoLanguages } from '@flecto/translations'

import { useDocumentState } from '../hooks/useDocumentState'
import { type DocumentPreviewData } from '../types/DocumentPreviewData'
export type DocumentType = 'contract' | 'quote' | 'proforma'

type FetchDocumentDataOptions = {
  bookingId: string
  documentType: DocumentType
}

export const serializeHTMLAndCSS = async (
  element: HTMLElement,
  document_id: string
): Promise<string> => {
  // Wait for next frame to ensure styles are applied
  await new Promise(requestAnimationFrame)

  // Function to wait for font loading
  const waitForFonts = async () => {
    try {
      await document.fonts.ready

      const fontLinks = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]')
      ).filter(
        (link) =>
          (link as HTMLLinkElement).href.includes('fonts.googleapis.com') ||
          (link as HTMLLinkElement).href.includes('fonts.gstatic.com')
      )

      // Wait for font stylesheets to load
      await Promise.all(
        fontLinks.map(
          (link) =>
            new Promise((resolve) => {
              if ((link as HTMLLinkElement).sheet) {
                resolve(true)
              } else {
                link.addEventListener('load', () => resolve(true))
                link.addEventListener('error', () => resolve(false))
              }
            })
        )
      )
    } catch (e) {
      console.warn('Error waiting for fonts:', e)
    }
  }

  await Promise.all([
    Promise.all(
      Array.from(document.styleSheets).map((sheet) => {
        if (sheet.href) {
          return new Promise((resolve) => {
            const link = document.querySelector(`link[href="${sheet.href}"]`)
            if (link) {
              if ((link as HTMLLinkElement).sheet) {
                resolve(true)
              } else {
                link.addEventListener('load', () => resolve(true))
                link.addEventListener('error', () => resolve(false))
              }
            } else {
              resolve(false)
            }
          })
        }
        return Promise.resolve(true)
      })
    ),

    waitForFonts(),
  ])

  const getAllStyles = () => {
    const styles: string[] = []
    const fontImports: string[] = []

    try {
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          const rules = Array.from(sheet.cssRules || [])
          rules.forEach((rule) => {
            // Handle @import rules separately to maintain font imports
            if (rule instanceof CSSImportRule) {
              fontImports.push(rule.cssText)
            } else {
              styles.push(rule.cssText)
            }
          })
        } catch (e) {
          if (sheet.href) {
            console.warn('External stylesheet reference:', sheet.href)
          }
        }
      })
    } catch (e) {
      console.error('Error collecting styles:', e)
    }

    return [...fontImports, ...styles].join('\n')
  }

  const html = element.outerHTML
  const styles = getAllStyles()

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${document_id}</title><style>${styles}</style></head><body><div id="document-content">${html}</div></body></html>`
}

const getDocumentPreview = async (
  documentType: DocumentType,
  bookingId: string
): Promise<DocumentPreviewData> => {
  try {
    switch (documentType) {
      case 'contract':
        return await getContractPreview(bookingId)
      case 'quote':
        return await getQuotePreview(bookingId)
      case 'proforma':
        return await getProformaPreview(bookingId)
      default:
        throw new Error(`Unknown document type: ${documentType}`)
    }
  } catch (error) {
    console.error(`Error fetching ${documentType} preview:`, error)
    throw error
  }
}

async function fetchInitialData(bookingId: string, documentType: DocumentType) {
  if (!documentType) {
    console.error('Error fetching initial data: documentType is undefined.')
    throw new Error('documentType is undefined')
  }

  try {
    const [documentPreview, taxProfileResult] = await Promise.all([
      getDocumentPreview(documentType, bookingId),
      getUserTaxProfile(),
    ])

    const host = process.env['FLECTO_LINK_URL'] ?? 'https://flec.to'

    const permalink =
      (documentPreview as DocumentPreviewData)?.booking?.permalink ?? ''

    const bookingPublicLink = `${host}/${permalink}`

    return {
      documentPreview,
      bookingPublicLink,
      taxProfile: taxProfileResult,
    }
  } catch (error) {
    console.error('Error fetching initial data:', error)
    throw error
  }
}

export async function fetchDocumentData({
  bookingId,
  documentType,
}: FetchDocumentDataOptions): Promise<void> {
  try {
    useDocumentState.getState().reset()

    const initialData = await fetchInitialData(bookingId, documentType)

    const customerLanguage =
      (initialData.documentPreview?.customer?.locale as FlectoLanguages) ||
      FlectoLanguages.DEFAULT

    useDocumentState.setState({
      documentPreview: initialData.documentPreview,
      bookingPublicLink: initialData.bookingPublicLink,
      customerLanguage,
      taxes: initialData.taxProfile,
      isLoaded: true,
    })
  } catch (error) {
    console.error('Error fetching document data:', error)
    throw error
  }
}
